import { AlertCircle, ArrowLeft, ArrowRight, Check, Search, X } from "lucide-react"
import * as React from "react"

import { profileSummary } from "@/components/domain/detector-profile"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/alert"
import { Checkbox } from "@/components/ui/checkbox"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"
import { DatePicker, formatDateShort, type DateStr } from "@/components/ui/date-picker"
import {
  FileDropzone,
  type AcceptedFile,
  type DropzoneStatus,
  type RejectReason,
} from "@/components/ui/file-dropzone"
import { Fieldset, FormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

import { BatchChoiceField, type BatchChoice } from "./batch-choice"
import { DetectorSettingsGroup, type DetectorSettingsGroupProps } from "./detector-settings-group"

/**
 * Three-step intake for a new review.
 *
 *   1  the printed label      what shipped, and when it was printed
 *   2  the approved master    which one governs, and why that one
 *   3  confirm                who it is for, and what happens next
 *
 * Step 2 is where the product's central rule lives. The printed date
 * decides which master governs — a label is only misbranded relative to
 * the master in force when it was printed, not the one approved after.
 * The wizard NAMES the chosen master and why, and shows what else
 * existed. A reviewer who accepts the wrong master here gets a review
 * full of correct-looking findings against the wrong document.
 *
 * Presentation: opens as a centered modal (focus for the decision),
 * with a minimize control that shrinks it to a side sheet so the queue
 * remains scannable when the reviewer wants to check something behind.
 */

function todayIso(): DateStr {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export interface MasterCandidate {
  id: string
  label: string
  stampedOn: string
  reason?: string
  supersededBy?: string
  pages?: number
  source: "ppls" | "upload" | "library"
}

/** One master attached to the review, from the registry or from an upload. */
export interface AttachedMaster {
  /** The PPLS URL, or the file name for an upload. Unique within the list. */
  id: string
  label: string
  /** Stamp date, or file size — whatever identifies it at a glance. */
  detail: string
  source: "ppls" | "upload"
}

export type ResolveState =
  | { kind: "idle" }
  | { kind: "resolving" }
  | {
      kind: "resolved"
      /** The registration these answered for, which the field may have moved past. */
      regno: string
      /** Every master on record for the registration, newest first. */
      candidates: MasterCandidate[]
      /**
       * The candidate the pipeline is expected to treat as governing, carrying
       * its reason on `.reason`. Null when no candidate can be placed against
       * the printed date. Marks a row; attaching it stays the reviewer's call.
       */
      governing: MasterCandidate | null
    }
  | { kind: "unreachable" }
  | { kind: "not_found" }

export interface NewReviewSheetProps {
  open: boolean
  onOpenChange: (o: boolean) => void

  printedFile: AcceptedFile | null
  printedStatus?: DropzoneStatus
  printedReason?: RejectReason
  /** True while the label is being read. A few seconds, once per chosen file. */
  identifying?: boolean
  /** Product name read off the artwork, shown beside the file it came from. */
  identifiedName?: string | null
  /** The label could not be read. The fields below are typeable, so this is not fatal. */
  identifyFailed?: boolean
  onPrintedSelect: (f: File) => void
  onPrintedRemove: () => void
  regNo: string
  onRegNoChange: (v: string) => void
  regNoError?: string
  printedOn: DateStr | null
  onPrintedOnChange: (d: DateStr | null) => void

  resolve: ResolveState
  onResolve: () => void
  /** URLs of the PPLS masters attached, in the order they were picked. */
  selectedMasterIds: readonly string[]
  onToggleMaster: (id: string) => void
  /** Uploaded masters, in attach order. The dropzone takes one at a time. */
  masterFiles: readonly File[]
  masterStatus?: DropzoneStatus
  onMasterSelect: (f: File) => void
  onMasterRemove: (name: string) => void

  clients: ComboboxOption[]
  /** True while the engagement list is in flight. Unknown is not empty. */
  clientsLoading?: boolean
  /**
   * Whether this principal may choose the engagement. Listing them is
   * admin-only on the backend, so anyone else gets the explanation below
   * instead of a picker that could only ever be empty.
   */
  canPickClient: boolean
  /** A client principal's reviews are filed against their own engagement. */
  isClient?: boolean
  clientId: string | null
  onClientChange: (id: string | null) => void

  /**
   * Undefined means the deployment decides by page count, which is what the
   * endpoint does when the field is omitted. `false` runs now at full price,
   * `true` sends it overnight at half.
   */
  batch: BatchChoice
  onBatchChange: (b: BatchChoice) => void
  /** Null when the lane is off here, or the policy has not answered yet. */
  batchPolicy: { pageThreshold: number; ceilingHours: number } | null
  /** Pages in the printed label, when they could be counted. */
  pageCount: number | null
  /** The detector settings group. Null for a principal who does not choose them. */
  detectorSettings: DetectorSettingsGroupProps | null

  submitting?: boolean
  /** Why the server refused. Shown on the confirm step, where the button is. */
  submitError?: string
  onSubmit: () => void
}

const STEPS = ["Printed label", "Approved master", "Confirm"] as const

export function NewReviewSheet(props: NewReviewSheetProps): JSX.Element {
  const [step, setStep] = React.useState(0)

  // The container clears the form on close, but the step lives here. Without
  // this, reopening lands on the step last left with nothing filled in.
  React.useEffect(() => {
    if (!props.open) setStep(0)
  }, [props.open])

  const canLeave1 = Boolean(props.printedFile) && props.printedStatus === "ready"
  // No gate on step 2: a review with no master attached is valid, and the
  // server resolves the contemporaneous one during the run.
  const canLeave2 = true
  const hasMaster = props.selectedMasterIds.length > 0 || props.masterFiles.length > 0

  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent
        size="lg"
        title="Start a review"
        description="The printed label, the approved master it should match, and who it is for."
        maximizable
        defaultMaximized
        footer={
          <div className="flex w-full items-center gap-2">
            <Button
              variant="ghost"
              leadingIcon={<ArrowLeft />}
              disabled={step === 0}
              onClick={() => {
                setStep((s) => s - 1)
              }}
            >
              Back
            </Button>
            <span className="flex-1" />
            {step < 2 ? (
              <Button
                variant="primary"
                trailingIcon={<ArrowRight />}
                disabled={step === 0 ? !canLeave1 : !canLeave2}
                onClick={() => {
                  setStep((s) => s + 1)
                }}
              >
                {/* The label names the outcome, because leaving step 2 empty
                    hands the choice of master to the server rather than
                    postponing it. */}
                {step === 1 && !hasMaster ? "Skip — resolve from EPA" : "Continue"}
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!canLeave1}
                loading={props.submitting ?? false}
                loadingText="Starting…"
                onClick={props.onSubmit}
              >
                Start the review
              </Button>
            )}
          </div>
        }
      >
        <div className="px-5 py-4">
          <Steps current={step} onGo={setStep} canLeave1={canLeave1} canLeave2={canLeave2} />

          {step === 0 && <StepPrinted {...props} />}
          {step === 1 && <StepMaster {...props} />}
          {step === 2 && <StepConfirm {...props} />}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function Steps({
  current,
  onGo,
  canLeave1,
  canLeave2,
}: {
  current: number
  onGo: (i: number) => void
  canLeave1: boolean
  canLeave2: boolean
}): JSX.Element {
  const reachable = [true, canLeave1, canLeave1 && canLeave2]
  return (
    <ol className="mb-5 flex flex-wrap items-center justify-center gap-1.5">
      {STEPS.map((label, i) => {
        const done = i < current
        const now = i === current
        return (
          <li key={label} className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!reachable[i]}
              aria-current={now ? "step" : undefined}
              onClick={() => {
                onGo(i)
              }}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1",
                "text-caption transition-colors duration-120 ease-out",
                now && "border-brand bg-brand-tint text-brand font-semibold",
                done && "border-master/40 bg-master-tint text-master",
                !now && !done && "border-border text-text-3",
                "disabled:pointer-events-none disabled:opacity-45",
              )}
            >
              <span className="grid size-4 place-items-center rounded-full font-id text-micro">
                {done ? <Check className="size-3" aria-hidden="true" /> : i + 1}
              </span>
              {label}
            </button>
            {i < STEPS.length - 1 && <span aria-hidden="true" className="h-px w-4 bg-border" />}
          </li>
        )
      })}
    </ol>
  )
}

function StepPrinted(p: NewReviewSheetProps): JSX.Element {
  return (
    <Fieldset
      legend="The printed label"
      description="The market label as printed. Born-digital PDFs read best; a scan escalates rather than being guessed at."
    >
      <FileDropzone
        label="Final printed label"
        file={p.printedFile}
        status={p.printedStatus}
        reason={p.printedReason}
        onSelect={p.onPrintedSelect}
        onRemove={p.onPrintedRemove}
      />

      {p.identifying && (
        <div className="mt-2 flex items-center gap-2 text-caption text-text-3">
          <span className="size-1.5 animate-pulse rounded-full bg-brand motion-reduce:animate-none" />
          Reading the registration number and printed date off the artwork…
        </div>
      )}
      {!p.identifying && p.identifiedName && (
        <div className="mt-2 text-caption text-text-3">
          Identified as <span className="text-text-2">{p.identifiedName}</span>
        </div>
      )}
      {!p.identifying && p.identifyFailed && (
        <p className="mt-2 max-w-[60ch] text-caption leading-normal text-text-3">
          Could not read the label automatically — fill the fields in below and the review runs
          exactly the same.
        </p>
      )}

      <div className="mt-3.5 grid grid-cols-2 gap-3.5">
        <FormField
          label="EPA Reg. No."
          hint="Read off the printed label, or leave it blank and let the pipeline find it."
          error={p.regNoError}
        >
          <Input
            mono
            inputMode="numeric"
            placeholder="65331-4"
            value={p.regNo}
            onChange={(e) => {
              p.onRegNoChange(e.target.value)
            }}
          />
        </FormField>

        <FormField
          label="Printed date"
          hint="Decides which master version governs — a label is only misbranded relative to the master in force when it was printed."
        >
          <DatePicker value={p.printedOn} onChange={p.onPrintedOnChange} max={todayIso()} />
        </FormField>
      </div>
    </Fieldset>
  )
}

/**
 * The registry search, present in every resolve state.
 *
 * The registration is what the search runs on, so it stays editable after a
 * result: a search that found nothing is the moment a reviewer is most likely
 * to have a number to correct.
 */
function MasterSearch(p: NewReviewSheetProps): JSX.Element {
  const searching = p.resolve.kind === "resolving"

  return (
    <div className="flex items-end gap-2">
      <FormField label="EPA Reg. No." className="flex-1">
        <Input
          mono
          inputMode="numeric"
          placeholder="65331-4"
          value={p.regNo}
          disabled={searching}
          onChange={(e) => {
            p.onRegNoChange(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && p.regNo.trim() && !searching) {
              p.onResolve()
            }
          }}
        />
      </FormField>
      <Button
        variant="primary"
        leadingIcon={<Search />}
        loading={searching}
        loadingText="Searching…"
        disabled={!p.regNo.trim() || searching}
        onClick={p.onResolve}
      >
        Find masters
      </Button>
    </div>
  )
}

/**
 * The registry result: every master on record, with the governing one marked.
 *
 * Several can be attached, because a label often draws its language from more
 * than one approved master and the endpoint accepts them all. Order carries
 * meaning — the first attached is the one judged against — so the row the
 * printed date points to is marked and gives its reason, and a reviewer who
 * picks another is overriding something stated rather than guessing.
 */
function MasterPicker({
  candidates,
  governing,
  regno,
  selectedIds,
  onToggle,
}: {
  candidates: MasterCandidate[]
  governing: MasterCandidate | null
  /** The registration these answered for, which need not be what is typed now. */
  regno: string
  selectedIds: readonly string[]
  onToggle: (id: string) => void
}): JSX.Element {
  return (
    <div className="mt-3">
      <p className="text-body-sm leading-normal text-text-2">
        {candidates.length} stamped {candidates.length === 1 ? "document" : "documents"} for {regno}
        . Newest first — attach the one in force when the label was printed, plus any later approved
        change it already reflects.
      </p>

      {/* Capped and scrolled: a registration can carry fifteen or more versions,
          and a list that tall pushes the upload target and the footer off the
          panel. */}
      <ul className="mt-2.5 max-h-64 overflow-y-auto rounded-lg border border-border">
        {candidates.map((m) => {
          const isGoverning = governing?.id === m.id
          const checked = selectedIds.includes(m.id)
          return (
            <li key={m.id} className="border-b border-border-soft last:border-b-0">
              <label
                className={cn(
                  "flex cursor-pointer items-start gap-3 px-3.5 py-2.5 transition-colors",
                  checked ? "bg-brand-tint/40" : "hover:bg-surface-sunken",
                )}
              >
                <Checkbox
                  className="mt-0.5"
                  checked={checked}
                  onCheckedChange={() => {
                    onToggle(m.id)
                  }}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-body-sm text-text">{m.stampedOn}</span>
                    {isGoverning && (
                      <span className="rounded-full bg-master-tint px-1.5 py-0.5 font-id text-micro uppercase tracking-[0.08em] text-master">
                        Governs
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate font-id text-micro text-text-3">
                    {m.label}
                  </span>
                  {isGoverning && m.reason && (
                    <span className="mt-1 block max-w-[52ch] text-caption leading-normal text-text-3">
                      {m.reason}
                    </span>
                  )}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * What is going with the review, in the order the endpoint will read it.
 *
 * Attaching happens in two places — ticking a registry row and dropping a file
 * — so neither control can show the whole answer. Order decides which master
 * the label is judged against, and a list is the only way to see it without
 * scrolling back through the candidates.
 */
function AttachedMasters({
  attached,
  governingId,
  onRemove,
}: {
  attached: readonly AttachedMaster[]
  governingId: string | null
  /** Omitted on the confirm step, where the list is read rather than edited. */
  onRemove?: (item: AttachedMaster) => void
}): JSX.Element | null {
  if (attached.length === 0) return null
  const first = attached[0]

  return (
    <div className="mt-4">
      <div className="flex items-baseline gap-2">
        <span className="text-body-sm font-semibold text-text">{attached.length} attached</span>
        <span className="text-caption text-text-3">judged against the first</span>
      </div>

      <ul className="mt-2 flex flex-col gap-1.5">
        {attached.map((item, i) => (
          <li
            key={item.id}
            className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2"
          >
            <span className="font-id text-micro text-text-3">{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body-sm text-text">{item.label}</span>
              <span className="font-id text-micro text-text-3">{item.detail}</span>
            </span>
            {item.id === governingId && (
              <span className="rounded-full bg-master-tint px-1.5 py-0.5 font-id text-micro uppercase tracking-[0.08em] text-master">
                Governs
              </span>
            )}
            {onRemove && (
              <IconButton
                label={`Remove ${item.label}`}
                size="sm"
                variant="ghost"
                icon={<X />}
                onClick={() => {
                  onRemove(item)
                }}
              />
            )}
          </li>
        ))}
      </ul>

      {/* The endpoint judges against the first attached, which is not
          necessarily the one the printed date points to. Saying so is the
          difference between an informed override and an accident. */}
      {governingId !== null && first !== undefined && first.id !== governingId && (
        <p className="mt-2 max-w-[60ch] text-caption leading-normal text-print-strong">
          The review will be judged against {first.label}, not the master in force on the printed
          date.{onRemove ? " Remove it to fall back to the marked one." : ""}
        </p>
      )}
    </div>
  )
}

/**
 * Everything attached, in the order the endpoint will read it.
 *
 * Two controls attach — ticking a registry row and dropping a file — and the
 * first item is what the label is judged against, so both the picker and the
 * confirm summary read this rather than each assembling their own answer.
 */
function attachedMasters(p: NewReviewSheetProps): AttachedMaster[] {
  const fromRegistry: AttachedMaster[] =
    p.resolve.kind === "resolved"
      ? p.selectedMasterIds.flatMap((id) => {
          const c =
            p.resolve.kind === "resolved"
              ? p.resolve.candidates.find((candidate) => candidate.id === id)
              : undefined
          return c
            ? [
                {
                  id: c.id,
                  label: c.label,
                  detail: `stamped ${c.stampedOn}`,
                  source: "ppls" as const,
                },
              ]
            : []
        })
      : []

  return [
    ...fromRegistry,
    ...p.masterFiles.map((f) => ({
      id: f.name,
      label: f.name,
      detail: "uploaded",
      source: "upload" as const,
    })),
  ]
}

function StepMaster(p: NewReviewSheetProps): JSX.Element {
  const r = p.resolve
  const attached = attachedMasters(p)

  return (
    <Fieldset
      legend="The approved master"
      description="The document this label is judged against. Everything downstream depends on getting this one right."
    >
      <MasterSearch {...p} />

      {r.kind === "idle" && (
        <p className="mt-3 max-w-[64ch] text-body-sm leading-normal text-text-2">
          {p.printedOn
            ? "Search the EPA registry for the master in force on the printed date, or upload it below."
            : "Without a printed date the registry can only offer the newest master, which may not be the one that governed. Go back and add the date if you have it."}
        </p>
      )}

      {r.kind === "resolved" && (
        <MasterPicker
          candidates={r.candidates}
          governing={r.governing}
          regno={r.regno}
          selectedIds={p.selectedMasterIds}
          onToggle={p.onToggleMaster}
        />
      )}

      <AttachedMasters
        attached={attached}
        governingId={r.kind === "resolved" ? (r.governing?.id ?? null) : null}
        onRemove={(item) => {
          if (item.source === "upload") p.onMasterRemove(item.id)
          else p.onToggleMaster(item.id)
        }}
      />

      {r.kind === "unreachable" && (
        <div className="mt-3 rounded-lg border border-err/30 bg-err-tint/40 p-4">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-err" aria-hidden="true" />
            <div>
              <div className="text-body-sm font-semibold text-err">
                The EPA registry is not reachable from here
              </div>
              <p className="mt-1 max-w-[58ch] text-body-sm leading-normal text-text-2">
                The registry only accepts US traffic, and this request was refused. That is an
                egress problem, not a problem with the label — upload the master below, or continue
                and let the review resolve it.
              </p>
            </div>
          </div>
        </div>
      )}

      {r.kind === "not_found" && (
        <div className="mt-3 rounded-lg border border-border p-4">
          <div className="text-body-sm font-semibold text-text">
            Nothing found for that registration number
          </div>
          <p className="mt-1 max-w-[58ch] text-body-sm leading-normal text-text-2">
            The registry search is uneven — an exact number sometimes returns nothing even when the
            label exists. Try the product name in the Label Finder, or upload the master if you
            already have it.
          </p>
        </div>
      )}

      <div className="mt-3.5">
        <FileDropzone
          compact
          label="Approved master"
          hint={
            p.masterFiles.length > 0
              ? "Add another if the label draws on more than one approved master."
              : "Upload the stamped master PDF yourself instead of searching."
          }
          file={null}
          status={p.masterStatus}
          onSelect={p.onMasterSelect}
          onRemove={() => {
            // The zone holds nothing; removal happens on the attached list.
          }}
        />
      </div>

      {/* Continuing with nothing attached is a common and complete path, so it
          is described here, where the reviewer decides. */}
      {attached.length === 0 && (
        <p className="mt-3 max-w-[64ch] text-body-sm leading-normal text-text-3">
          You can continue without one. The review resolves the master that was in force on the
          printed date from the EPA registry, and escalates to a specialist rather than guessing if
          it cannot.
        </p>
      )}
    </Fieldset>
  )
}

function StepConfirm(p: NewReviewSheetProps): JSX.Element {
  const attached = attachedMasters(p)
  const governingId = p.resolve.kind === "resolved" ? (p.resolve.governing?.id ?? null) : null

  return (
    <Fieldset
      legend="Who it is for"
      description="Attaching the review to a client is what lets their login see it. Leave it off for internal work."
    >
      {p.canPickClient && (
        <FormField
          label="Client"
          optional
          hint="Membership on the Clients screen scopes access. Nothing else grants it."
        >
          {p.clientsLoading ? (
            <Skeleton className="h-9 w-full" />
          ) : (
            <Combobox
              options={p.clients}
              value={p.clientId}
              onValueChange={p.onClientChange}
              placeholder={
                p.clients.length === 0
                  ? "No engagements yet — create one under Clients"
                  : "Internal work — no client"
              }
              searchPlaceholder="Search engagements"
            />
          )}
        </FormField>
      )}

      {p.isClient && (
        <p className="text-body-sm leading-normal text-text-2">
          This will be filed against your engagement.
        </p>
      )}

      {!p.canPickClient && !p.isClient && (
        <p className="text-body-sm leading-normal text-text-2">
          This review is internal. An administrator can file it against a client engagement
          afterwards.
        </p>
      )}

      {p.batchPolicy && (
        <BatchChoiceField
          value={p.batch}
          onChange={p.onBatchChange}
          pageThreshold={p.batchPolicy.pageThreshold}
          ceilingHours={p.batchPolicy.ceilingHours}
          pages={p.pageCount}
        />
      )}

      {p.detectorSettings && <DetectorSettingsGroup {...p.detectorSettings} />}

      <div className="mt-5 rounded-lg border border-border bg-surface-sunken p-4">
        <div className="font-id text-micro uppercase tracking-[0.1em] text-text-3">
          What will run
        </div>
        <dl className="mt-2.5 flex flex-col gap-2">
          <Row k="Printed label" v={p.printedFile?.name ?? "—"} />
          {p.identifiedName && <Row k="Product" v={p.identifiedName} />}
          <Row k="EPA Reg. No." v={p.regNo || "read during the review"} />
          <Row k="Printed on" v={p.printedOn ? formatDateShort(p.printedOn) : "not given"} />
          {attached.length === 0 && (
            <Row
              k="Judged against"
              v="the master in force on the printed date"
              note="resolved from the EPA registry when the review runs"
            />
          )}
          {p.detectorSettings?.options && (
            <Row
              k="Detector settings"
              v={profileSummary(p.detectorSettings.value, p.detectorSettings.options)}
            />
          )}
          <Row
            k="Visible to"
            v={
              p.clientId
                ? (p.clients.find((c) => c.value === p.clientId)?.label ?? "—")
                : "the review team only"
            }
          />
        </dl>
        <AttachedMasters attached={attached} governingId={governingId} />

        <p className="mt-3 border-t border-border pt-2.5 text-caption leading-normal text-text-3">
          You can leave this screen — findings appear in the workspace as each stage completes, and
          anything the review cannot determine is escalated for a specialist rather than guessed.
        </p>
      </div>

      {p.submitError && (
        <div role="alert" className="mt-3 rounded-lg border border-err/30 bg-err-tint/40 p-3.5">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-err" aria-hidden="true" />
            <div>
              <div className="text-body-sm font-semibold text-err">The review was not started</div>
              <p className="mt-1 max-w-[58ch] text-body-sm leading-normal text-text-2">
                {p.submitError}
              </p>
            </div>
          </div>
        </div>
      )}
    </Fieldset>
  )
}

function Row({ k, v, note }: { k: string; v: string; note?: string }): JSX.Element {
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3">
      <dt className="text-caption text-text-3">{k}</dt>
      <dd className="min-w-0">
        <span className="block truncate text-body-sm text-text">{v}</span>
        {note && <span className="font-id text-micro text-text-3">{note}</span>}
      </dd>
    </div>
  )
}
